import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { Activity, AlertCircle, TrendingUp, ExternalLink, Zap, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import { Toast, ToastType } from '../common/Toast';

interface InfrastructureMetrics {
  functionsDeployed: number;
  activeAlerts: number;
  uptime30Days: number;
  avgResponseTimeMs: number;
  lastUpdated: string;
}

interface ToastState {
  message: string;
  type: ToastType;
  visible: boolean;
}

export function GoldenSignalsDashboard() {
  const [metrics, setMetrics] = useState<InfrastructureMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<ToastState>({ message: '', type: 'info', visible: false });

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const getMetrics = httpsCallable<void, InfrastructureMetrics>(
        functions,
        'getInfrastructureMetrics'
      );
      const result = await getMetrics();
      setMetrics(result.data);
    } catch (error) {
      console.error('Failed to fetch infrastructure metrics:', error);
      // Fallback to defaults
      setMetrics({
        functionsDeployed: 74,
        activeAlerts: 0,
        uptime30Days: 99.9,
        avgResponseTimeMs: 250,
        lastUpdated: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type, visible: true });
  };

  const refreshMetrics = async () => {
    try {
      setRefreshing(true);
      const refresh = httpsCallable<void, { success: boolean; metrics: InfrastructureMetrics; message: string }>(
        functions,
        'refreshInfrastructureMetrics'
      );
      const result = await refresh();
      setMetrics(result.data.metrics);
      showToast(result.data.message, 'success');
    } catch (error: any) {
      console.error('Failed to refresh metrics:', error);
      showToast(`Failed to refresh metrics: ${error.message}`, 'error');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, visible: false })}
        />
      )}
      <div className="p-6 min-h-screen bg-slate-900">
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 opacity-90"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-white mb-3">System Monitoring</h1>
              <p className="text-white opacity-80 text-lg">Real-time infrastructure health and performance metrics</p>
            </div>
            <div className="flex items-center gap-4">
              {metrics && (
                <div className="text-right text-white text-sm opacity-70">
                  <p>Last updated:</p>
                  <p>{new Date(metrics.lastUpdated).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}</p>
                </div>
              )}
              <button
                onClick={refreshMetrics}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh Metrics'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && !metrics ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-6 shadow-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-16 -translate-y-16"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <CheckCircle className="w-6 h-6 text-white opacity-60" />
              </div>
              <div className="text-white opacity-70 text-sm font-medium mb-1">Functions Deployed</div>
              <div className="text-4xl font-bold text-white">{metrics?.functionsDeployed || 74}</div>
              <div className="text-white opacity-60 text-xs mt-2">All systems operational</div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 p-6 shadow-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-16 -translate-y-16"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <CheckCircle className="w-6 h-6 text-white opacity-60" />
              </div>
              <div className="text-white opacity-70 text-sm font-medium mb-1">Active Alerts</div>
              <div className="text-4xl font-bold text-white">{metrics?.activeAlerts || 0}</div>
              <div className="text-white opacity-60 text-xs mt-2">Monitoring policies active</div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 p-6 shadow-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-16 -translate-y-16"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <CheckCircle className="w-6 h-6 text-white opacity-60" />
              </div>
              <div className="text-white opacity-70 text-sm font-medium mb-1">Uptime (30d)</div>
              <div className="text-4xl font-bold text-white">{metrics?.uptime30Days || 99.9}%</div>
              <div className="text-white opacity-60 text-xs mt-2">Exceeding SLA targets</div>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-red-600 p-6 shadow-2xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full transform translate-x-16 -translate-y-16"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <CheckCircle className="w-6 h-6 text-white opacity-60" />
              </div>
              <div className="text-white opacity-70 text-sm font-medium mb-1">Avg Response</div>
              <div className="text-4xl font-bold text-white">{metrics?.avgResponseTimeMs || 250}ms</div>
              <div className="text-white opacity-60 text-xs mt-2">Optimal performance</div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4 pb-8">
        
         <a href="https://console.cloud.google.com/monitoring/dashboards/custom/850a58b1-0c7d-4330-b7dd-8bd3368f3c5d?project=valutten-dev"
          target="_blank"
          rel="noopener noreferrer"
          className="group block relative overflow-hidden rounded-xl bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700 hover:border-blue-500 transition-all shadow-xl"
        >
          <div className="relative p-6 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <Activity className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">
                  Cloud Monitoring Dashboard
                </h3>
                <p className="text-slate-400">Real-time metrics for all {metrics?.functionsDeployed || 74} cloud functions</p>
              </div>
            </div>
            <ExternalLink className="w-6 h-6 text-slate-500 group-hover:text-blue-400 transition-colors" />
          </div>
        </a>

        
         <a href="https://console.cloud.google.com/monitoring/alerting/policies?project=valutten-dev"
          target="_blank"
          rel="noopener noreferrer"
          className="group block relative overflow-hidden rounded-xl bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700 hover:border-purple-500 transition-all shadow-xl"
        >
          <div className="relative p-6 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
                <AlertCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-purple-400 transition-colors">
                  Alert Policies
                </h3>
                <p className="text-slate-400">{metrics?.activeAlerts || 0} active policies monitoring your infrastructure</p>
              </div>
            </div>
            <ExternalLink className="w-6 h-6 text-slate-500 group-hover:text-purple-400 transition-colors" />
          </div>
        </a>

        
         <a href="https://console.cloud.google.com/errors?project=valutten-dev"
          target="_blank"
          rel="noopener noreferrer"
          className="group block relative overflow-hidden rounded-xl bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700 hover:border-orange-500 transition-all shadow-xl"
        >
          <div className="relative p-6 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="p-4 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-orange-400 transition-colors">
                  Error Reporting
                </h3>
                <p className="text-slate-400">Track and debug production errors in real-time</p>
              </div>
            </div>
            <ExternalLink className="w-6 h-6 text-slate-500 group-hover:text-orange-400 transition-colors" />
          </div>
        </a>
      </div>
    </div>
    </>
  );
}